<template>
    <div v-if="isOpen" class="modal fade show" aria-labelledby="voucherDetailTitle" style="display: block; background: #1c1d1c33">
        <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center px-2 py-1">
                    <h2 class="mb-0">{{ voucherObj ? voucherObj.name : 'Voucher Details' }}</h2>
                    <button
                        @click="closeModal"
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                    ></button>
                </div>
                <hr class="mt-0">

                <div class="modal-body">
                    <div v-if="!voucherObj" class="alert alert-danger text-center py-4">
                        Invalid voucher please check again
                    </div>

                    <template v-else-if="voucher">
                        <p><b>Date: </b> {{ voucher.date }}</p>
                        <p><b>Voucher Number: </b> {{ voucher.voucher_no }}</p>

                        <table class="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th>Account Head</th>
                                    <th>Description</th>
                                    <th>Party Name</th>
                                    <th>Debit</th>
                                    <th>Credit</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ledger in voucher.ledgers" :key="ledger.id">
                                    <td>{{ ledger.head.name }}</td>
                                    <td>{{ ledger.note }}</td>
                                    <td>{{ ledger.party ? ledger.party.name : '' }}</td>
                                    <td class="text-right px-2">{{ ledger.debit ? commaFormat(ledger.debit) : '' }}</td>
                                    <td class="text-right px-2">{{ ledger.credit ? commaFormat(ledger.credit) : '' }}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="my-2">
                            <p><b>Total Amount: </b> {{ commaFormat(voucher.total_amount) }}</p>
                            <p v-if="voucher.note"><b>Voucher Note: </b> {{ voucher.note }}</p>

                            <div v-if="approvalLogs.length > 0">
                                <div
                                    class="alert py-1 border px-2"
                                    v-for="log in approvalLogs"
                                    :key="log.id"
                                    :class="approvalLogClasses(log.status)"
                                >
                                    <p class="mb-0">
                                        Status: <strong class="capitalize">{{ log.status }}</strong>
                                    </p>
                                    <p class="text-dark font-small-3 mb-0">
                                        Approved By: <strong class="capitalize">{{ log.approved_by_user.name }}</strong>
                                    </p>
                                    <p class="text-dark mb-0" v-if="log.note">
                                        Note: {{ log.note }}
                                    </p>
                                </div>
                            </div>

                            <template v-if="!disableButton">
                                <div class="form-group mb-2">
                                    <label for="approval-note" class="mb-1 fw-bolder">Approval Note:</label>
                                    <textarea
                                        name="approval-note"
                                        id="approval-note"
                                        class="form-control"
                                        rows="5"
                                        v-model="approvalNote"
                                    ></textarea>
                                </div>

                                <div class="d-flex align-items-center justify-content-end gap-2 mx-2">
                                    <button class="btn btn-success" @click="handleApprovalOrRejection()">Approve</button>
                                    <button class="btn btn-info" @click="handleApprovalOrRejection('return')">Return</button>
                                    <button class="btn btn-danger" @click="handleApprovalOrRejection('rejected')">Reject</button>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useVoucherPrint } from "@/services/utils/voucherPrint"
import { computed, inject, ref } from "vue"
import { useRoute } from "vue-router"
import figureFormatter from "@/services/utils/figureFormatter"
import handleJournal from "@/services/modules/journal"

const emit = defineEmits(['approved'])

const { vouchers } = useVoucherPrint()
const { commaFormat } = figureFormatter()
const { handleApproval } = handleJournal()
const $route = useRoute()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const isOpen = ref(false)
const voucher = ref(null)
const approvalNote = ref('')
const voucherType = ref(null)
const txnType = ref(null)

const voucherObj = computed(() => {
    if (voucherType.value && vouchers[voucherType.value]) {
        return vouchers[voucherType.value]
    }
    return null
})

const disableButton = computed(() => {
    if (!voucher.value) return true
    if (isApprovalBlocked.value) return true
    return voucher.value.status !== 'pending_approval'
})

const isApprovalBlocked = computed(() => {
    return voucherType.value === 'journal_voucher' && txnType.value === 'bill_payment'
})

const approvalLogs = computed(() => {
    if (!voucher.value) return []
    return voucher.value.ledger_approval_logs ? voucher.value.ledger_approval_logs : []
})

const approvalLogClasses = (status) => {
    if (status === 'approved') return 'alert-success border-success'
    if (['rejected', 'cancelled'].includes(status)) return 'alert-danger border-danger'
    return 'alert-info border-info'
}

const getVoucher = (voucherId) => {
    if (!voucherObj.value) return
    voucherObj.value.vMethod(voucherId + '?with_log=1')
        .then(res => {
            voucher.value = res.data
        })
        .catch(() => {
            showError("Something went wrong please try again later")
        })
}

const openModal = (voucherId, type, txn = null) => {
    voucher.value = null
    approvalNote.value = ''
    voucherType.value = type
    txnType.value = txn
    isOpen.value = true
    getVoucher(voucherId)
}

const closeModal = () => {
    isOpen.value = false
}

const handleApprovalOrRejection = (status = 'approved') => {
    const note = approvalNote.value ? approvalNote.value.trim() : false

    if (status !== 'approved' && !note) {
        showError('Approval note is required if it is rejected or return')
        return
    }

    handleApproval({
        company_id: $route.params.companyId,
        voucher_ids: [voucher.value.id],
        note: !note ? null : note,
        status
    }).then(res => {
        showSuccess(res.message)
        closeModal()
        emit('approved')
    }).catch(err => {
        if (err.response && err.response.data) {
            showError(err.response.data.message)
            return
        }
        showError('Something went wrong please try again later')
    })
}

defineExpose({
    openModal
})
</script>
